"use client";

import { useState, useSyncExternalStore } from "react";
import { Check, Copy } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button, HStack, Stack, Text } from "@leabankab/component-library";
import { StepContainer } from "@/components/layout";
import { Heading } from "@/components/ui";

const NON_FLOW_SEGMENTS = new Set(["tink", "dev", "api"]);

const readFlowIdFromPath = (): string | null => {
	const segment = window.location.pathname.split("/").filter(Boolean)[0];
	if (!segment || NON_FLOW_SEGMENTS.has(segment)) return null;
	try {
		return decodeURIComponent(segment);
	} catch {
		return segment;
	}
};

const readFlowIdOnServer = (): string | null => null;

const subscribeToPathChanges = (onStoreChange: () => void) => {
	window.addEventListener("popstate", onStoreChange);
	return () => window.removeEventListener("popstate", onStoreChange);
};

export const NotFoundContent = () => {
	const t = useTranslations("pages.NotFoundPage");
	const tips = t.raw("tips") as string[];
	const [copied, setCopied] = useState(false);
	const flowId = useSyncExternalStore(
		subscribeToPathChanges,
		readFlowIdFromPath,
		readFlowIdOnServer
	);

	const handleCopy = () => {
		if (!flowId) return;
		navigator.clipboard
			.writeText(flowId)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			})
			.catch(() => {});
	};

	return (
		<StepContainer>
			<Stack gap={3} align="stretch">
				<Stack gap={1.5}>
					<Heading level={1} textStyle="heading-lg">
						{t("title")}
					</Heading>
					<Text color="text.subtle">{t("description")}</Text>
				</Stack>

				<div className="rounded-xl bg-bg-subtle p-2.5">
					<Text
						render={<span />}
						fontWeight="medium"
						color="text.base"
						className="mb-1.5 block uppercase"
					>
						{t("tipsTitle")}
					</Text>
					<Stack render={<ul />} gap={1.25} className="m-0 list-disc pl-2.5">
						{tips.map((tip) => (
							<Text render={<li />} textStyle="body-sm" key={tip}>
								{tip}
							</Text>
						))}
					</Stack>
				</div>

				{flowId && (
					<Stack gap={1}>
						<Text
							render={<span />}
							fontWeight="medium"
							color="text.subtle"
							className="uppercase"
						>
							{t("flowIdLabel")}
						</Text>
						<HStack
							gap={1}
							align="center"
							className="rounded-xl border border-border-base py-0.5 pr-1 pl-2"
						>
							<Text textStyle="body-sm" className="flex-1 truncate font-mono">
								{flowId}
							</Text>
							<Button
								variant="ghost"
								size="sm"
								onClick={handleCopy}
								aria-label={t("copy")}
								startIcon={
									copied ? (
										<Check size={16} aria-hidden="true" />
									) : (
										<Copy size={16} aria-hidden="true" />
									)
								}
							>
								{copied ? t("copied") : t("copy")}
							</Button>
						</HStack>
						<Text render={<span />} color="text.subtle">
							{t("flowIdHelp")}
						</Text>
					</Stack>
				)}
			</Stack>
		</StepContainer>
	);
};
